import React, { startTransition } from "react";
import {
  Button,
  ButtonClear,
  ButtonExpansion,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  useMediaQuery,
} from "@salestrak/ui";

import { EntityTag, type EntityTagProps } from "#app/components/entity";
import { useAppData } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

const BUTTON_LABEL = "Only Me";

type CurrentUserButtonSize = "xs" | "sm" | "default" | "lg";

type CurrentUserButtonProps = Omittable<{
  selected: boolean;
  onClick: () => void;
  onClearClick?: () => void;
  size?: CurrentUserButtonSize;
  /** Pin the compact (avatar-only) face — never expand to the "Only Me" pill.
   *  For tight chrome like a widget card header where the pill would overrun. */
  compact?: boolean;
  /** Hover tooltip, doubling as the accessible name. Override where the
   *  me-mode filters something other than projects. */
  tooltip?: string;
}>;

// The avatar is the pill's leading GLYPH: 16px on every face, the same box
// as the svg glyphs in the neighbouring toolbar buttons, so the me-mode chip
// sits on the toolbar's icon line rather than reading as a larger blob. The
// Button pads an avatar-led expansion pill to centre exactly this box.
const AVATAR_SIZE: NonNullable<EntityTagProps["size"]> = "2xs";

export const CurrentUserButton = React.memo(function CurrentUserButton({
  selected,
  onClick,
  onClearClick,
  size = "default",
  compact = false,
  tooltip = "Filter for your Projects",
}: CurrentUserButtonProps) {
  const { currentUser } = useAppData();
  const isMobile = useIsMobile();
  const isCoarsePointer = useMediaQuery({ pointer: "coarse" });
  const isDesktop = !isMobile && !isCoarsePointer;
  const [visualSelected, setVisualSelected] = React.useState(selected);

  React.useEffect(() => {
    setVisualSelected(selected);
  }, [selected]);

  const handlePress = React.useCallback(() => {
    // Desktop resolves to "on" because the clear × in the expanded pill is
    // the off switch — except in compact mode, which never expands, so the
    // press itself must toggle (as it does on touch).
    const nextSelected = isDesktop && !compact ? true : !selected;
    setVisualSelected(nextSelected);

    startTransition(() => {
      onClick();
    });
  }, [compact, isDesktop, onClick, selected]);

  const handleClear = React.useCallback(() => {
    setVisualSelected(false);

    startTransition(() => {
      onClearClick?.();
    });
  }, [onClearClick]);

  const avatar = (
    <EntityTag
      entity={{
        kind: "user",
        id: currentUser?.userId ?? "me",
        user: currentUser ?? null,
      }}
      display="visual"
      size={AVATAR_SIZE}
      interactive={false}
    />
  );

  const button = (
    <Button
      size={size}
      color={visualSelected ? "primary" : "neutral"}
      variant={visualSelected ? "outline" : "ghost"}
      aria-pressed={visualSelected}
      // A phone's toolbar has no room for the "Only Me" pill: the chip keeps
      // its avatar-only footprint there and the tint alone carries the ON
      // state, so the toolbar row never wraps and pushes its tail off screen.
      expanded={visualSelected && !compact && !isMobile}
      onClick={handlePress}
      aria-label={tooltip}
    >
      {avatar}
      <ButtonExpansion>
        {/* Inherits the chip's rung: 12px on the 24px `xs` face, 14px above. */}
        <Text size={null} weight="medium" truncate>
          {BUTTON_LABEL}
        </Text>
      </ButtonExpansion>
      {visualSelected && onClearClick && !isMobile ? (
        // The × disc reveals only under `data-expanded` on hover, so a phone,
        // where the press itself toggles, never mounts it.
        // It carries its own tooltip: the app-wide delay group closes
        // the chip's bubble as this one opens, so the labels hand off.
        <Tooltip>
          <TooltipTrigger
            render={
              <ButtonClear
                aria-label="Clear current user filter"
                onClick={handleClear}
              />
            }
          />
          <TooltipPopup side="top">Clear</TooltipPopup>
        </Tooltip>
      ) : null}
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
});

CurrentUserButton.displayName = "CurrentUserButton";
